import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/axios";
import { FaPlusCircle, FaFileInvoiceDollar } from "react-icons/fa";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const ReceiptList = () => {
  const navigate = useNavigate();
  const { canView, canCreate } = usePermissions();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  if (!canView("receipts")) {
    return <AccessDenied moduleName="Receipts" action="view" />;
  }

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const res = await api.get("receipts/");
      let data = Array.isArray(res.data) ? res.data : (res.data?.receipts || []);
      
      // Filter by type if in URL
      const searchParams = new URLSearchParams(window.location.search);
      const typeFilter = searchParams.get("type");
      if (typeFilter) {
        data = data.filter(r => r.party_type === typeFilter);
      }
      
      setReceipts(data);
    } catch (err) {
      console.error("Error fetching receipts:", err);
      setError("Failed to load receipts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, [location.search]); // Refresh on URL change

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "AED",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) return (
    <div className="container mt-4 text-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-2">Loading receipts...</p>
    </div>
  );

  return (
    <div className="container-fluid mt-4 px-4">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="mb-0 text-[#0077cc] fw-bold d-flex align-items-center gap-2">
              <FaFileInvoiceDollar /> Receipts List
            </h3>
            <div>
              {canCreate("receipts") && (
              <button 
                className="btn btn-primary d-flex align-items-center gap-2" 
                onClick={() => navigate("/dashboard/receipts/new")}
              >
                <FaPlusCircle /> Add Receipt
              </button>
              )}
            </div>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <div className="table-responsive">
            <table className="table table-hover align-middle" style={{fontSize: "0.9rem"}}>
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Party (Customer/Supplier)</th>
                  <th>Sale ID</th>
                  <th>Mode</th>
                  <th>Bank / Cheque</th>
                  <th>Amount</th>
                  <th>Tax</th>
                  <th>Total</th>
                  <th>Recon. Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {receipts.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      No receipts found.
                    </td>
                  </tr>
                ) : (
                  receipts.map((receipt) => (
                    <tr key={receipt.id}>
                      <td><strong>#{receipt.receipt_number || receipt.id}</strong></td>
                      <td>{formatDate(receipt.payment_date)}</td>
                      <td>
                        <span className={`badge ${receipt.party_type === 'Customer' ? 'bg-primary' : 'bg-warning text-dark'} me-2`}>
                          {receipt.party_type}
                        </span>
                        {receipt.customer_name || receipt.supplier_name || "—"}
                      </td>
                      <td>{receipt.sales_id ? `#${receipt.sales_id}` : "—"}</td>
                      <td>{receipt.payment_mode || "—"}</td>
                      <td>
                        {receipt.bank_name && <div className="small">Bank: {receipt.bank_name}</div>}
                        {receipt.cheque_number && <div className="small text-muted">Chq: {receipt.cheque_number}</div>}
                      </td>
                      <td className="fw-bold">{formatCurrency(receipt.amount_paid)}</td>
                      <td className="text-muted">{formatCurrency(receipt.tax_amount)}</td>
                      <td className="fw-bold text-success">
                        {formatCurrency(Number(receipt.amount_paid) + Number(receipt.tax_amount))}
                      </td>
                      <td>
                        {["Bank Transfer", "Cheque"].includes(receipt.payment_mode) ? (
                          receipt.is_reconciled ? (
                            <span className="badge" style={{background:"#d4edda",color:"#155724",fontSize:"0.78rem",padding:"4px 8px",borderRadius:"12px"}}>
                              ✓ Reconciled
                            </span>
                          ) : (
                            <span className="badge" style={{background:"#fff3cd",color:"#856404",fontSize:"0.78rem",padding:"4px 8px",borderRadius:"12px"}}>
                              ⏳ Unreconciled
                            </span>
                          )
                        ) : (
                          <span className="text-muted" style={{fontSize:"0.78rem"}}>N/A</span>
                        )}
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/dashboard/receipts/view/${receipt.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptList;
